import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, Clock, Trophy, ArrowRight, Target, TrendingUp, TrendingDown, Minus, Sparkles, Calendar, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { PageContainer } from '../../components/shared/PageContainer';
import { PageHero, HeroStatPill } from '../../components/shared/PageHero';
import { itemVariants } from '../../components/shared/animations';
import { Skeleton } from '../../components/shared/Skeleton';
import { DjayAvatar } from '../../mascot';
import { useDjaySelection } from '../../mascot';
import { GroupId } from '../../config/systemConfig';
import { error as logError } from '../../utils/logger';
import { WorldZone } from '../../components/shared/WorldZone';
import { AssessmentConfig, AssessmentDomainId, AssessmentHistoryEntry, AssessmentReport, SubTestDefinition, TeacherAssignment } from '../types';
import type { CooldownResult } from '../types';
import { getAssessmentHistory, checkDomainCooldown, getReport } from '../utils/assessmentStorage';
import { getAvailableSubTests, DOMAIN_META, DOMAIN_MODULE_MAP } from '../utils/assessmentDomainConfig';
import { dataLayer } from '../../data/dataLayer';
const DEFAULT_QUESTION_COUNT = 30;
const URGENT_DEADLINE_MS = 2 * 24 * 60 * 60 * 1000;
interface AssessmentPageProps {
  selectedGroup: GroupId;
  onStart: (config: AssessmentConfig) => void;
  onViewReport: (report: AssessmentReport) => void;
  learnerId?: string;
}
export function AssessmentPage({
  selectedGroup,
  onStart,
  onViewReport,
  learnerId
}: AssessmentPageProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const [history, setHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [reports, setReports] = useState<Record<string, AssessmentReport>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showAllResults, setShowAllResults] = useState(false);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [domainCooldowns, setDomainCooldowns] = useState<Record<AssessmentDomainId, CooldownResult>>({
    rekenen: {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    },
    taalverzorging: {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    },
    'begrijpend-lezen': {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    }
  });
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setIsLoading(true);
      try {
        const h = await getAssessmentHistory(selectedGroup);
        if (isMounted) setHistory(h);
        const loadedReports: Record<string, AssessmentReport> = {};
        await Promise.all(h.map(async entry => {
          const report = await getReport(entry.attemptId);
          if (report) loadedReports[entry.attemptId] = report;
        }));
        if (isMounted) setReports(loadedReports);
        if (learnerId) {
          const assignmentsData = await dataLayer.getTeacherAssignments();
          const allAssignments = Object.values(assignmentsData);
          const myAssignments = allAssignments.filter(a => {
            if (a.status !== 'active') return false;
            if (a.deadline && Date.now() > a.deadline) return false;
            const completedBy = a.completedByStudentIds || [];
            if (completedBy.includes(learnerId!)) return false;
            if (a.wholeClass && a.group === selectedGroup) return true;
            if (a.targetStudentIds && a.targetStudentIds.includes(learnerId)) return true;
            return false;
          });
          if (isMounted) setAssignments(myAssignments.sort((a, b) => b.createdAt - a.createdAt));
        }
      } catch (e) {
        logError('Failed to load assessment history', e);
      }
      try {
        const domains: AssessmentDomainId[] = ['rekenen', 'taalverzorging', 'begrijpend-lezen'];
        const cooldowns: Record<string, CooldownResult> = {};
        await Promise.all(domains.map(async domainId => {
          cooldowns[domainId] = await checkDomainCooldown(selectedGroup, domainId);
        }));
        if (isMounted) setDomainCooldowns(cooldowns as Record<AssessmentDomainId, CooldownResult>);
      } catch (e) {
        logError('Failed to check cooldowns', e);
      }
      if (isMounted) setIsLoading(false);
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [selectedGroup, learnerId]);
  const handleStartSubTest = (subTest: SubTestDefinition) => {
    onStart({
      questionCount: subTest.questionCount,
      moduleFilter: subTest.modules,
      minModules: 1,
      countsForMastery: false,
      seed: Date.now(),
      domainId: subTest.domainId
    });
  };
  const handleStartAssignment = (assignment: TeacherAssignment) => {
    const modules = DOMAIN_MODULE_MAP[assignment.domainId as keyof typeof DOMAIN_MODULE_MAP] || null;
    onStart({
      domainId: assignment.domainId,
      mode: assignment.type === 'targeted' ? 'targeted' : 'general',
      targetFamilyIds: assignment.selectedFamilyIds || [],
      assignmentId: assignment.id,
      questionCount: assignment.questionCount || DEFAULT_QUESTION_COUNT,
      moduleFilter: modules,
      minModules: 1,
      countsForMastery: false,
      seed: Date.now()
    });
  };
  const hasHistory = history.length > 0;
  const bestScore = hasHistory ? Math.max(...history.map(h => h.score)) : null;
  const availableSubTests = getAvailableSubTests(selectedGroup);
  const displayedHistory = showAllResults ? history : history.slice(0, 5);
  const anyDomainOnCooldown = Object.values(domainCooldowns).some(c => !c.canStart);
  const maxRemainingMs = Math.max(...Object.values(domainCooldowns).map(c => c.remainingMs));
  return <PageContainer className="space-y-5 sm:space-y-6 pb-20" data-id="element-883">
      {/* Hero */}
      <motion.div variants={itemVariants} data-id="element-884">
        <PageHero title="Toetsen" subtitle={hasHistory ? 'Laat zien hoeveel je gegroeid bent!' : 'Ontdek waar je staat met een toets!'} gradientClasses="from-orange-500 via-amber-500 to-yellow-500" djayEmotion={hasHistory ? 'excited' : 'encouraging'} djayVariant={selectedVariant} icon={ClipboardCheck} iconBgClass="bg-orange-400" floatingEmojis={['📝', '⭐']} data-id="element-885">
          <HeroStatPill icon={Target} data-id="element-886">Groep {selectedGroup}</HeroStatPill>
          {hasHistory && <HeroStatPill icon={ClipboardCheck} data-id="element-887">
              {history.length} toets{history.length !== 1 ? 'en' : ''} gemaakt
            </HeroStatPill>}
          {bestScore !== null && <HeroStatPill icon={Trophy} data-id="element-888">Topscore: {bestScore}%</HeroStatPill>}
        </PageHero>
      </motion.div>

      {/* Teacher-Assigned Assessments */}
      {assignments.length > 0 && <motion.div variants={itemVariants} data-id="element-889">
          <WorldZone title="Van je Leerkracht" icon={Sparkles} accentColor="bg-indigo-100 text-indigo-600" djayMessage="Je leerkracht heeft een toets voor je klaargezet. Doe rustig je best!" djayEmotion="pointing" djayVariant={selectedVariant} data-id="element-890">
            <div className="space-y-3" data-id="element-891">
              {assignments.map(assignment => {
            const meta = DOMAIN_META[assignment.domainId as keyof typeof DOMAIN_META];
            const Icon = meta?.icon;
            const hasDeadline = assignment.deadline;
            const isUrgent = hasDeadline && assignment.deadline - Date.now() < URGENT_DEADLINE_MS;
            return <button key={assignment.id} onClick={() => handleStartAssignment(assignment)} className={`w-full text-left rounded-2xl p-4 border-2 transition-all hover:shadow-md flex items-center gap-4 ${isUrgent ? 'border-orange-300 bg-orange-50/50' : 'border-white bg-white hover:border-indigo-200'}`} data-id="element-892">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${meta?.bgColor || 'bg-slate-100'} ${meta?.color || 'text-slate-500'}`} data-id="element-893">
                      {Icon ? <Icon className="w-6 h-6" data-id="element-894" /> : <ClipboardCheck className="w-6 h-6" data-id="element-895" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-896">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-897">
                        <h3 className="font-black text-base text-slate-900" data-id="element-898">
                          {assignment.domainLabel}
                        </h3>
                        {assignment.type === 'targeted' && <Badge variant="default" className="bg-purple-100 text-purple-700 border-purple-200 text-[10px]" data-id="element-899">
                            Gericht
                          </Badge>}
                        {isUrgent && <Badge variant="default" className="bg-orange-100 text-orange-700 border-orange-300 text-[10px] animate-pulse" data-id="element-900">
                            Bijna deadline!
                          </Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 font-medium" data-id="element-901">
                        <span className="flex items-center gap-1" data-id="element-902">
                          <Clock className="w-3 h-3" data-id="element-903" />
                          ~15 min
                        </span>
                        {hasDeadline && <span className={`flex items-center gap-1 ${isUrgent ? 'text-orange-600 font-bold' : ''}`} data-id="element-904">
                            <Calendar className="w-3 h-3" data-id="element-905" />
                            {new Date(assignment.deadline).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'short'
                    })}
                          </span>}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-indigo-400 flex-shrink-0" data-id="element-906" />
                  </button>;
          })}
            </div>
          </WorldZone>
        </motion.div>}

      {/* Niveaubepaling */}
      <motion.div variants={itemVariants} data-id="element-907">
        <WorldZone title="Ontdek je Niveau" icon={Target} accentColor="bg-orange-100 text-orange-600" djayMessage="Kies een toets om te laten zien wat je al kan! Geen stress, gewoon je best doen." djayEmotion="encouraging" djayVariant={selectedVariant} data-id="element-908">
          {isLoading ? <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-909">
              {[1, 2, 3].map(i => <div key={i} className="bg-white/50 rounded-2xl p-4 border-2 border-white" data-id="element-910">
                  <Skeleton className="w-10 h-10 rounded-xl mb-3" data-id="element-911" />
                  <Skeleton className="h-5 w-3/4 mb-2" data-id="element-912" />
                  <Skeleton className="h-4 w-full mb-3" data-id="element-913" />
                  <Skeleton className="h-3 w-20" data-id="element-914" />
                </div>)}
            </div> : <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-915">
              {availableSubTests.map((subTest, i) => {
            const meta = DOMAIN_META[subTest.domainId];
            const cooldown = domainCooldowns[subTest.domainId];
            const canStartThis = cooldown?.canStart !== false && !isLoading;
            return <motion.div key={subTest.domainId} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.08
            }} data-id="element-916">
                    <button onClick={() => canStartThis && handleStartSubTest(subTest)} disabled={!canStartThis} className={`w-full text-left rounded-2xl p-4 border-2 transition-all flex flex-col h-full ${!canStartThis ? 'opacity-50 cursor-not-allowed border-slate-200 bg-slate-50' : `bg-white hover:shadow-lg hover:-translate-y-0.5 ${meta.borderColor}`}`} data-id="element-917">
                      <div className="flex items-center gap-3 mb-3" data-id="element-918">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${meta.bgColor} ${meta.color}`} data-id="element-919">
                          <meta.icon className="w-5 h-5" data-id="element-920" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-921">
                          <h3 className={`font-black text-base ${meta.color}`} data-id="element-922">
                            {subTest.label}
                          </h3>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 font-medium mb-3 flex-grow leading-relaxed" data-id="element-923">
                        {meta.description}
                      </p>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500 font-medium" data-id="element-924">
                        <span className="flex items-center gap-1" data-id="element-925">
                          <ClipboardCheck className="w-3 h-3" data-id="element-926" />
                          {subTest.questionCount}v
                        </span>
                        <span className="flex items-center gap-1" data-id="element-927">
                          <Clock className="w-3 h-3" data-id="element-928" />~
                          {subTest.estimatedMinutes}m
                        </span>
                        {subTest.shortNote && <span className={`px-1.5 py-0.5 rounded ${meta.bgColor} ${meta.color} font-bold`} data-id="element-929">
                            {subTest.shortNote}
                          </span>}
                      </div>
                      {cooldown && !cooldown.canStart && <div className="mt-2 text-[11px] font-bold px-2 py-1 rounded-md bg-slate-100 text-slate-500 inline-block" data-id="element-930">
                          ⏳ Nog {formatRemainingTime(cooldown.remainingMs)}
                        </div>}
                    </button>
                  </motion.div>;
          })}
            </div>}

          {anyDomainOnCooldown && <div className="mt-4 bg-white/60 border border-white rounded-xl p-3 flex items-center gap-3 text-sm text-slate-600 font-medium" data-id="element-931">
              <span className="text-lg" data-id="element-932">⏳</span>
              <span data-id="element-933">
                Je hebt recent een toets gemaakt. Nog{' '}
                <strong data-id="element-934">{formatRemainingTime(maxRemainingMs)}</strong> wachten.
              </span>
            </div>}
        </WorldZone>
      </motion.div>

      {/* Results */}
      <motion.div variants={itemVariants} data-id="element-935">
        <WorldZone title="Jouw Resultaten" icon={Trophy} accentColor="bg-amber-100 text-amber-600" djayMessage={hasHistory ? 'Kijk eens hoe goed je het hebt gedaan!' : 'Na je eerste toets zie je hier je resultaten!'} djayEmotion={hasHistory ? 'celebrating' : 'happy'} djayVariant={selectedVariant} isLast={true} data-id="element-936">
          {isLoading ? <div className="space-y-2" data-id="element-937">
              {[1, 2, 3].map(i => <div key={i} className="bg-white/50 rounded-xl p-3 border border-white flex items-center gap-3" data-id="element-938">
                  <Skeleton className="w-10 h-10 rounded-xl" data-id="element-939" />
                  <div className="flex-1" data-id="element-940">
                    <Skeleton className="h-4 w-32 mb-1" data-id="element-941" />
                    <Skeleton className="h-3 w-48" data-id="element-942" />
                  </div>
                </div>)}
            </div> : hasHistory ? <div className="space-y-2" data-id="element-943">
              {displayedHistory.map((entry, index) => {
            const date = new Date(entry.completedAt);
            const dateStr = date.toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short'
            });
            const report = reports[entry.attemptId];
            const prevEntry = history.slice(index + 1).find(h => h.domainId === entry.domainId);
            const domainMeta = entry.domainId ? DOMAIN_META[entry.domainId] : null;
            const isAssigned = !!entry.assignmentId;
            // Score color
            const scoreBg = entry.score >= 70 ? 'bg-emerald-500' : entry.score >= 50 ? 'bg-amber-500' : 'bg-rose-500';
            // Trend
            let trendEl = null;
            if (prevEntry) {
              if (entry.score > prevEntry.score) {
                trendEl = <TrendingUp className="w-3.5 h-3.5 text-emerald-500" data-id="element-944" />;
              } else if (entry.score < prevEntry.score) {
                trendEl = <TrendingDown className="w-3.5 h-3.5 text-rose-400" data-id="element-945" />;
              }
            }
            return <div key={entry.attemptId} className="bg-white rounded-xl p-3 flex items-center gap-3 hover:shadow-sm transition-shadow border border-slate-100" data-id="element-946">
                    {/* Score circle */}
                    <div className={`w-11 h-11 rounded-xl ${scoreBg} flex items-center justify-center flex-shrink-0`} data-id="element-947">
                      <span className="text-white font-black text-sm" data-id="element-948">
                        {entry.score}%
                      </span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0" data-id="element-949">
                      <div className="flex items-center gap-1.5" data-id="element-950">
                        <span className="text-sm font-bold text-slate-900" data-id="element-951">
                          {domainMeta ? domainMeta.shortLabel : 'Toets'}
                        </span>
                        {domainMeta && <span className={`w-4 h-4 rounded flex items-center justify-center ${domainMeta.bgColor} ${domainMeta.color}`} data-id="element-952">
                            <domainMeta.icon className="w-2.5 h-2.5" data-id="element-953" />
                          </span>}
                        {trendEl}
                        {isAssigned && <Badge variant="default" className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[9px] px-1 py-0" data-id="element-954">
                            Leerkracht
                          </Badge>}
                      </div>
                      <p className="text-[11px] text-slate-400 font-medium mt-0.5" data-id="element-955">
                        {entry.totalQuestions} vragen · {dateStr}
                      </p>
                    </div>

                    {/* Action */}
                    {report && !isAssigned ? <button onClick={() => onViewReport(report)} className="text-xs font-bold text-amber-600 hover:text-amber-700 flex items-center gap-1 flex-shrink-0" data-id="element-956">
                        Rapport <ArrowRight className="w-3 h-3" data-id="element-957" />
                      </button> : isAssigned ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-958" /> : null}
                  </div>;
          })}

              {history.length > 5 && <button onClick={() => setShowAllResults(!showAllResults)} className="w-full text-center text-xs font-bold text-slate-500 hover:text-slate-700 py-2" data-id="element-959">
                  {showAllResults ? 'Toon minder' : `Toon alle resultaten (${history.length})`}
                </button>}
            </div> : <div className="text-center py-6" data-id="element-960">
              <div className="text-4xl mb-2" data-id="element-961">🌟</div>
              <p className="text-slate-800 font-bold mb-1" data-id="element-962">
                Nog geen resultaten
              </p>
              <p className="text-sm text-slate-500 font-medium" data-id="element-963">
                Start hierboven een toets om te zien waar je staat.
              </p>
            </div>}
        </WorldZone>
      </motion.div>
    </PageContainer>;
}
function formatRemainingTime(ms: number): string {
  const minutes = Math.ceil(ms / (60 * 1000));
  if (minutes <= 1) return 'minder dan een minuut';
  if (minutes < 60) return `${minutes} minuten`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours} uur`;
  return `${hours} uur en ${remainingMinutes} minuten`;
}